import { useEffect, useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

// Mirrors the backend defaults in features5.DELTA_DEFAULTS.
const DEFAULTS = {
  Haemoglobin: [2, "absolute"], "Platelet Count": [50, "percent"], "WBC Count": [50, "percent"],
  "PCV / HCT": [6, "absolute"], "RBC Count": [25, "percent"], INR: [30, "percent"],
  "Absolute Neutrophil Count": [50, "percent"],
};

/** Warns when a repeat result swings past that parameter's delta limit. */
export const DeltaWarning = ({ patient, testName, results }) => {
  const [prev, setPrev] = useState(null);
  const [params, setParams] = useState([]);

  useEffect(() => {
    if (!patient || !testName) { setPrev(null); return; }
    api.get("/samples/previous", { params: { patient, test_name: testName } })
      .then(({ data }) => setPrev(data.found ? data : null))
      .catch(() => setPrev(null));
    api.get(`/panels/${encodeURIComponent(testName)}`)
      .then(({ data }) => setParams(data.parameters || []))
      .catch(() => setParams([]));
  }, [patient, testName]);

  if (!prev) return null;

  const limitFor = (name) => {
    const p = params.find((x) => x.name === name);
    if (p && p.delta_limit !== null && p.delta_limit !== undefined) {
      return [Number(p.delta_limit), p.delta_type || "percent"];
    }
    return DEFAULTS[name] || null;
  };

  const rows = Object.entries(prev.results || {})
    .map(([name, oldVal]) => {
      const rule = limitFor(name);
      if (!rule) return null;
      const [limit, mode] = rule;
      const now = parseFloat(String(results?.[name] ?? "").replace(/[^\d.-]/g, ""));
      const before = parseFloat(String(oldVal).replace(/[^\d.-]/g, ""));
      if (Number.isNaN(now) || Number.isNaN(before)) return null;
      const diff = now - before;
      const change = mode === "absolute"
        ? Math.abs(diff)
        : (before === 0 ? 0 : (Math.abs(diff) / Math.abs(before)) * 100);
      if (change <= limit) return null;
      return {
        name, before, now, up: diff > 0,
        text: mode === "absolute"
          ? `${diff > 0 ? "+" : "−"}${Math.round(Math.abs(diff) * 100) / 100} (limit ${limit})`
          : `${diff > 0 ? "+" : "−"}${Math.round(change)}% (limit ${limit}%)`,
      };
    })
    .filter(Boolean);

  return (
    <div className="sm:col-span-2 rounded-md border border-[#002FA7]/30 bg-[#EFF6FF] p-3" data-testid="delta-warning">
      <p className="text-xs text-[#111827]">
        Previous {testName} for this patient was reported on <b>{prev.reported_on}</b> (sample {prev.sample_id}).
        {rows.length === 0 && " No parameter has crossed its delta limit."}
      </p>
      {rows.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="delta-list">
          {rows.map((r) => (
            <span key={r.name} data-testid={`delta-${r.name}`}
              className="inline-flex items-center gap-1 rounded-full border border-[#B45309]/40 bg-[#FFFBEB] px-2 py-0.5 text-[11px] text-[#B45309]">
              {r.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {r.name}: {r.before} → {r.now} {r.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
